import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import { api, setAccessToken, onUnauthenticated } from './api'
import { queryClient } from './queryClient'
import { setTenantDefaultTheme } from './theme'
import type { Branding, Tenant, User } from './types'

interface SessionResponse {
  accessToken: string
  expiresAt: string
  user: User
}

interface AuthState {
  user: User | null
  tenant: Tenant | null
  branding: Branding | null
  mapProvider: string
  mapApiKey: string
  loading: boolean
  signIn: (email: string, password: string) => Promise<void>
  signOut: () => Promise<void>
  impersonate: (tenantId: string, reason: string) => Promise<void>
  stopImpersonating: () => Promise<void>
  can: (cap: string) => boolean
  refreshProfile: () => Promise<void>
}

const Ctx = createContext<AuthState | null>(null)

export function useAuth() {
  const ctx = useContext(Ctx)
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>')
  return ctx
}

/** Shorthand for the very common `can('x') && <button/>` pattern. */
export function useCan() {
  return useAuth().can
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [tenant, setTenant] = useState<Tenant | null>(null)
  const [branding, setBranding] = useState<Branding | null>(null)
  const [map, setMap] = useState({ provider: 'osm', apiKey: '' })
  const [loading, setLoading] = useState(true)

  const refreshTimer = useRef<number | undefined>(undefined)

  const loadProfile = useCallback(async () => {
    const me = await api<{
      user: User
      tenant: Tenant
      map: { provider: string; apiKey: string }
    }>('/auth/me')
    setUser(me.user)
    setTenant(me.tenant)
    setMap({ provider: me.map?.provider ?? 'osm', apiKey: me.map?.apiKey ?? '' })
  }, [])

  /** Refresh a minute before the access token expires, so a dispatcher
   *  watching the map all shift is never bounced to the login screen. */
  const scheduleRefresh = useCallback((expiresAt: string) => {
    window.clearTimeout(refreshTimer.current)
    const ms = new Date(expiresAt).getTime() - Date.now() - 60_000
    refreshTimer.current = window.setTimeout(
      () => {
        void silentRefresh()
      },
      Math.max(ms, 15_000),
    )
  }, [])

  const silentRefresh = useCallback(async () => {
    try {
      const s = await api<SessionResponse>('/auth/refresh', { method: 'POST', noRetry: true })
      setAccessToken(s.accessToken)
      setUser(s.user)
      scheduleRefresh(s.expiresAt)
    } catch {
      setAccessToken(null)
      setUser(null)
    }
  }, [scheduleRefresh])

  // Public branding is fetched from the Host header before anything else, so
  // a white-label domain never flashes the platform's own colours first.
  useEffect(() => {
    void (async () => {
      try {
        const b = await api<Branding>('/branding/public')
        setBranding(b)
        applyBranding(b)
      } catch {
        // A branding failure must not block sign-in.
      }
    })()
  }, [])

  // Attempt to resume an existing session on load.
  useEffect(() => {
    void (async () => {
      try {
        const s = await api<SessionResponse>('/auth/refresh', { method: 'POST', noRetry: true })
        setAccessToken(s.accessToken)
        setUser(s.user)
        scheduleRefresh(s.expiresAt)
        await loadProfile()
      } catch {
        setAccessToken(null)
      } finally {
        setLoading(false)
      }
    })()

    return () => window.clearTimeout(refreshTimer.current)
  }, [loadProfile, scheduleRefresh])

  useEffect(() => {
    onUnauthenticated(() => {
      setAccessToken(null)
      setUser(null)
      setTenant(null)
      // Whatever is cached belongs to the session that just ended.
      queryClient.clear()
    })
  }, [])

  const signIn = useCallback(
    async (email: string, password: string) => {
      const s = await api<SessionResponse>('/auth/login', { body: { email, password } })
      // Nothing fetched before this sign-in may be shown to this person: a
      // previous user's lists would otherwise appear until they went stale.
      queryClient.clear()
      setAccessToken(s.accessToken)
      setUser(s.user)
      scheduleRefresh(s.expiresAt)
      await loadProfile()
    },
    [loadProfile, scheduleRefresh],
  )

  const signOut = useCallback(async () => {
    try {
      await api('/auth/logout', { method: 'POST' })
    } finally {
      window.clearTimeout(refreshTimer.current)
      setAccessToken(null)
      setUser(null)
      setTenant(null)
      queryClient.clear()
    }
  }, [])

  const impersonate = useCallback(
    async (tenantId: string, reason: string) => {
      const res = await api<{ accessToken: string; expiresAt: string }>('/auth/impersonate', {
        body: { tenantId, reason },
      })
      // Stepping into another account: its lists must be fetched afresh.
      queryClient.clear()
      setAccessToken(res.accessToken)
      scheduleRefresh(res.expiresAt)
      await loadProfile()
      setUser((u) => (u ? { ...u, impersonating: true } : u))
    },
    [loadProfile, scheduleRefresh],
  )

  const stopImpersonating = useCallback(async () => {
    const res = await api<{ accessToken: string; expiresAt: string }>('/auth/impersonate/stop', {
      method: 'POST',
    })
    // Back in our own account: drop the impersonated account's lists.
    queryClient.clear()
    setAccessToken(res.accessToken)
    scheduleRefresh(res.expiresAt)
    await loadProfile()
    setUser((u) => (u ? { ...u, impersonating: false } : u))
  }, [loadProfile, scheduleRefresh])

  // The capability set is the same one the server enforces; this only decides
  // what to render. Every call is checked again server-side.
  const caps = useMemo(() => new Set(user?.capabilities ?? []), [user])
  const can = useCallback((cap: string) => caps.has(cap), [caps])

  const value = useMemo<AuthState>(
    () => ({
      user,
      tenant,
      branding,
      mapProvider: map.provider,
      mapApiKey: map.apiKey,
      loading,
      signIn,
      signOut,
      impersonate,
      stopImpersonating,
      can,
      refreshProfile: loadProfile,
    }),
    [
      user,
      tenant,
      branding,
      map,
      loading,
      signIn,
      signOut,
      impersonate,
      stopImpersonating,
      can,
      loadProfile,
    ],
  )

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>
}

/**
 * Pushes a reseller's colours into the CSS custom properties the stylesheet
 * reads. Everything themeable is a variable, so this is the entire white-label
 * mechanism on the client.
 */
export function applyBranding(b: Branding) {
  const root = document.documentElement

  if (b.accentColor) {
    root.style.setProperty('--accent', b.accentColor)
    root.style.setProperty('--accent-dim', `${b.accentColor}33`)
    // Pick black or white for text on the accent by luminance, so a pale
    // brand colour does not produce white-on-white buttons.
    root.style.setProperty('--accent-ink', readableInk(b.accentColor))
  }
  if (b.primaryColor) {
    root.style.setProperty('--accent-2', b.primaryColor)
  }
  // The reseller's default applies only until the person picks their own.
  setTenantDefaultTheme(b.defaultTheme)
  if (b.productName) {
    document.title = `${b.productName} — Fleet Operations`
  }
  if (b.faviconUrl) {
    let link = document.querySelector<HTMLLinkElement>('link[rel="icon"]')
    if (!link) {
      link = document.createElement('link')
      link.rel = 'icon'
      document.head.appendChild(link)
    }
    link.href = b.faviconUrl
  }
}

function readableInk(hex: string): string {
  const m = /^#?([\da-f]{6})$/i.exec(hex.trim())
  if (!m) return '#06080b'
  const n = parseInt(m[1]!, 16)
  const r = (n >> 16) & 255
  const g = (n >> 8) & 255
  const b = n & 255
  // Rec. 709 luma; the 0.6 cut is where a mid-tone stops reading on black.
  const luma = (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255
  return luma > 0.6 ? '#06080b' : '#ffffff'
}

